'use client';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary } from '@mui/material';
import { ReactNode, useCallback, useRef } from 'react';
import { useCitationContext } from '../StructuredContentRenderer/components/Citation/context';
import { CITATION_LIST_ID } from './constants';
import { useHashScrollOnMount } from './useHashScrollOnMount';

type CitationListAccordionProps = {
  summary: ReactNode;
  children: ReactNode;
};

export const CitationListAccordion = ({ summary, children }: CitationListAccordionProps) => {
  const {
    citationListExpanded,
    setCitationListExpanded,
    shouldScrollToCitations,
    setShouldScrollToCitations,
  } = useCitationContext();
  const accordionRef = useRef<HTMLDivElement>(null);

  const handleExpand = useCallback(() => {
    setCitationListExpanded(true);
  }, [setCitationListExpanded]);

  // Handle initial page load with hash - expands accordion and scrolls after DOM stabilizes
  useHashScrollOnMount({ onExpand: handleExpand, hashKey: CITATION_LIST_ID });

  return (
    <Accordion
      ref={accordionRef}
      disableGutters
      elevation={0}
      square
      sx={{ border: 0 }}
      expanded={citationListExpanded}
      onChange={() => {
        setCitationListExpanded(!citationListExpanded);
      }}
      slotProps={{
        transition: {
          onEntered: () => {
            if (shouldScrollToCitations && window.location.hash === `#${CITATION_LIST_ID}`) {
              accordionRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
              setShouldScrollToCitations(false);
            }
          },
        },
      }}
      id={CITATION_LIST_ID}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ pl: 3 }}>
        {summary}
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0, pl: 3, pb: 3 }}>{children}</AccordionDetails>
    </Accordion>
  );
};
